import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

/**
 * Multas que el sistema no puede comunicar solo.
 *
 * Una sola lista, ordenada por lo que antes vence. Cada multa aparece una vez,
 * con un motivo. Las vencidas se cuentan pero no se listan hasta pedirlo: el
 * 18-sep-2026 eran 302 de 447 y enterraban las 5 que todavia se podian salvar,
 * que es como se llega a tener 300 avisos en rojo que nadie mira.
 */

// El texto dice QUE HACER, no que ha pasado. "Error del organismo" no le sirve
// a nadie; "falta el domicilio del arrendatario" se puede resolver.
const MOTIVOS = {
  falta_decidir:            { texto: 'Falta decidir a quién se identifica', pista: 'Nadie ha marcado todavía si responde el arrendatario o un conductor.' },
  sin_domicilio_cesionario: { texto: 'Falta el domicilio del arrendatario',  pista: 'Tiene el CIF, pero sin domicilio completo no se puede comunicar.' },
  sin_datos_cesionario:     { texto: 'Falta el CIF del arrendatario',        pista: 'Sin documento del cesionario no se puede identificar.' },
  sin_dni_conductor:        { texto: 'Falta el DNI del conductor',           pista: 'Identificada como conductor pero sin documento.' },
  firma_madrid:             { texto: 'Fallo de firma en Madrid',             pista: 'Problema de certificado o firma, no de datos: se reintenta.' },
  error_organismo:          { texto: 'El organismo la rechazó',              pista: 'Mira el error para ver si se puede reintentar.' },
  revision_manual:          { texto: 'Apartada para revisión manual',        pista: 'Alguien la sacó del automático; hay que decidir qué se hace.' },
  sin_identificar:          { texto: 'Sin clasificar',                       pista: 'No encaja en ningún motivo conocido: avisa para poder afinarlo.' }
};

const eur = (n) => (n || n === 0)
  ? Number(n).toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
  : '—';

function Plazo({ dias, vencida }) {
  if (vencida) return <span className="text-gray-500">Fuera de plazo</span>;
  if (dias <= 0) return <span className="font-semibold text-red-700">Vence hoy</span>;
  if (dias <= 2) return <span className="font-semibold text-red-700">{dias} d</span>;
  if (dias <= 5) return <span className="font-semibold text-orange-600">{dias} d</span>;
  return <span className="text-gray-700">{dias} d</span>;
}

export default function AdminMultasNoComunicables() {
  const navigate = useNavigate();
  const [datos, setDatos] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [paginacion, setPaginacion] = useState({ page: 1, totalPages: 1, total: 0 });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [motivo, setMotivo] = useState('todos');
  const [incluirVencidas, setIncluirVencidas] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [busquedaDebounced, setBusquedaDebounced] = useState('');
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => { setBusquedaDebounced(busqueda); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [busqueda]);

  // Los filtros viven en la URL para que la pagina se pueda compartir y para
  // que volver atras desde una multa no pierda el sitio.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get('motivo')) setMotivo(q.get('motivo'));
    if (q.get('empresa_id')) setEmpresaId(q.get('empresa_id'));
    if (q.get('incluir_vencidas') === '1') setIncluirVencidas(true);
  }, []);

  useEffect(() => {
    api.get('/admin/empresas?limit=200')
      .then(r => setEmpresas(r.data?.data || []))
      .catch(() => setEmpresas([]));
  }, []);

  const cargar = useCallback(async () => {
    setCargando(true); setError(null);
    try {
      const q = new URLSearchParams({ page, limit: 50, motivo, incluir_vencidas: incluirVencidas ? 'true' : 'false' });
      if (empresaId) q.set('empresa_id', empresaId);
      if (busquedaDebounced) q.set('busqueda', busquedaDebounced);
      const r = await api.get(`/admin/multas/no-comunicables?${q}`);
      setDatos(r.data.data || []);
      setResumen(r.data.resumen || null);
      setPaginacion(r.data.pagination || { page: 1, totalPages: 1, total: 0 });
      const url = new URLSearchParams();
      if (motivo !== 'todos') url.set('motivo', motivo);
      if (empresaId) url.set('empresa_id', empresaId);
      if (incluirVencidas) url.set('incluir_vencidas', '1');
      window.history.replaceState(null, '', `/admin/multas/no-comunicables${url.toString() ? '?' + url : ''}`);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'No se pudo cargar la lista');
    } finally {
      setCargando(false);
    }
  }, [page, motivo, incluirVencidas, empresaId, busquedaDebounced]);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="flex items-start justify-between gap-4 mb-1">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Multas que hay que tramitar a mano</h1>
          <p className="text-sm text-gray-600 mt-1">
            El sistema no puede comunicarlas solo. Ordenadas por lo que antes vence:
            el plazo de identificación es de 20 días desde la notificación.
          </p>
        </div>
        <button onClick={cargar} className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
          Actualizar
        </button>
      </div>

      {resumen && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-5">
          <Tarjeta n={resumen.criticas} label="Vencen en 2 días o menos" tono="rojo" />
          <Tarjeta n={resumen.urgentes} label="Vencen esta semana" tono="naranja" />
          <Tarjeta n={resumen.total_en_lista} label="En la lista" sub={eur(resumen.importe_en_lista)} />
          <Tarjeta n={resumen.vencidas} label="Fuera de plazo" sub={eur(resumen.importe_vencidas)} tono="gris" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Chip activo={motivo === 'todos'} onClick={() => { setMotivo('todos'); setPage(1); }}>Todos</Chip>
        {(resumen?.por_motivo || []).map(m => (
          <Chip key={m.motivo} activo={motivo === m.motivo} onClick={() => { setMotivo(m.motivo); setPage(1); }}>
            {(MOTIVOS[m.motivo]?.texto) || m.motivo}
            <span className="ml-1.5 text-xs opacity-70">{m.en_plazo}</span>
          </Chip>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          value={busqueda} onChange={e => setBusqueda(e.target.value)}
          placeholder="Matrícula, expediente u organismo"
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-72"
        />
        <select
          value={empresaId} onChange={e => { setEmpresaId(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">Todas las empresas</option>
          {empresas.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input type="checkbox" checked={incluirVencidas}
                 onChange={e => { setIncluirVencidas(e.target.checked); setPage(1); }} />
          Mostrar también las fuera de plazo
          {resumen?.vencidas > 0 && <span className="text-gray-500">({resumen.vencidas})</span>}
        </label>
      </div>

      {error && (
        <div className="p-4 mb-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-600">
            <tr>
              <th className="px-4 py-3 font-medium">Plazo</th>
              <th className="px-4 py-3 font-medium">Límite</th>
              <th className="px-4 py-3 font-medium">Matrícula</th>
              <th className="px-4 py-3 font-medium">Expediente</th>
              <th className="px-4 py-3 font-medium">Organismo</th>
              <th className="px-4 py-3 font-medium">Empresa</th>
              <th className="px-4 py-3 font-medium">Qué falta</th>
              <th className="px-4 py-3 font-medium text-right">Importe</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {cargando && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-500">Cargando…</td></tr>
            )}
            {!cargando && datos.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-600">
                No queda nada pendiente con estos filtros.
                {!incluirVencidas && resumen?.vencidas > 0 &&
                  <> Hay {resumen.vencidas} fuera de plazo que no se están mostrando.</>}
              </td></tr>
            )}
            {!cargando && datos.map(m => (
              <tr key={m.id}
                  className={`hover:bg-blue-50/50 cursor-pointer ${m.vencida ? 'text-gray-500' : ''}`}
                  onClick={() => navigate(`/admin/multas/${m.id}`)}>
                <td className="px-4 py-3 whitespace-nowrap">
                  <Plazo dias={m.dias_restantes} vencida={m.vencida} />
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                  {m.fecha_limite ? new Date(m.fecha_limite).toLocaleDateString('es-ES') : '—'}
                </td>
                <td className="px-4 py-3 font-medium">{m.matricula || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{m.numero_expediente || '—'}</td>
                <td className="px-4 py-3 text-gray-600 max-w-[220px] truncate" title={m.organismo_emisor || ''}>
                  {m.organismo_emisor || '—'}
                </td>
                <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate">{m.empresa_nombre || '—'}</td>
                <td className="px-4 py-3">
                  <span className="text-gray-900">{MOTIVOS[m.motivo]?.texto || m.motivo}</span>
                  <div className="text-xs text-gray-500">{MOTIVOS[m.motivo]?.pista}</div>
                  {m.dgt_error && (
                    <div className="text-xs text-gray-400 mt-0.5 max-w-[340px] truncate" title={m.dgt_error}>
                      {m.dgt_error}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">{eur(m.importe_multa)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    onClick={(ev) => { ev.stopPropagation(); navigate(`/admin/multas/${m.id}/editar`); }}
                    className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50">
                    Resolver
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {paginacion.totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
          <span>{paginacion.total} multas · página {paginacion.page} de {paginacion.totalPages}</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40">Anterior</button>
            <button disabled={page >= paginacion.totalPages} onClick={() => setPage(p => p + 1)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40">Siguiente</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Tarjeta({ n, label, sub, tono }) {
  const color = tono === 'rojo' ? 'text-red-700' : tono === 'naranja' ? 'text-orange-600'
              : tono === 'gris' ? 'text-gray-500' : 'text-gray-900';
  return (
    <div className="border border-gray-200 rounded-xl p-4 bg-white">
      <div className={`text-2xl font-semibold ${color}`}>{n ?? '—'}</div>
      <div className="text-sm text-gray-600 mt-0.5">{label}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

function Chip({ activo, onClick, children }) {
  return (
    <button onClick={onClick}
      className={`px-3 py-1.5 text-sm rounded-full border transition ${
        activo ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
      {children}
    </button>
  );
}
